/**
 * Excel Export Utility
 * Creates multi-sheet Excel workbooks with professional formatting
 * Uses xlsx library (SheetJS)
 */

import * as XLSX from 'xlsx';
import { generateFilename } from './csv-export';

export interface ExcelColumn {
  key: string;
  header: string;
  width?: number;
  transform?: (value: unknown, row?: Record<string, unknown>) => string | number;
}

export interface ExcelSheetConfig {
  name: string;
  data: Record<string, unknown>[];
  columns?: ExcelColumn[];
}

export interface ExcelExportOptions {
  carrierName?: string;
  dotNumber?: number;
  includeMetadata?: boolean;
  dateFormat?: string;
}

/**
 * Export data to Excel with multiple sheets
 */
export const exportToExcel = async (
  sheets: ExcelSheetConfig[],
  filename: string,
  options: ExcelExportOptions = {},
  onProgress?: (progress: number) => void
): Promise<void> => {
  const workbook = XLSX.utils.book_new();

  // Add metadata sheet if requested
  if (options.includeMetadata) {
    const metadataSheet = createMetadataSheet(options);
    XLSX.utils.book_append_sheet(workbook, metadataSheet, 'Report Info');
  }

  // Process each sheet
  for (let i = 0; i < sheets.length; i++) {
    const sheetConfig = sheets[i];

    if (onProgress) {
      onProgress(Math.round(((i + 1) / sheets.length) * 90));
    }

    if (sheetConfig.data.length === 0) {
      // Create empty sheet with headers only
      const emptySheet = XLSX.utils.aoa_to_sheet([
        sheetConfig.columns?.map(c => c.header) || ['No data available']
      ]);
      XLSX.utils.book_append_sheet(workbook, emptySheet, sanitizeSheetName(sheetConfig.name));
      continue;
    }

    // Transform data if columns are specified
    let sheetData: Record<string, unknown>[];
    if (sheetConfig.columns) {
      sheetData = sheetConfig.data.map(row => {
        const transformed: Record<string, unknown> = {};
        sheetConfig.columns!.forEach(col => {
          const value = getNestedValue(row, col.key);
          transformed[col.header] = col.transform
            ? col.transform(value, row)
            : formatValue(value);
        });
        return transformed;
      });
    } else {
      sheetData = sheetConfig.data;
    }

    // Create worksheet
    const worksheet = XLSX.utils.json_to_sheet(sheetData);

    // Set column widths
    if (sheetConfig.columns) {
      worksheet['!cols'] = sheetConfig.columns.map(col => ({
        wch: col.width || calculateColumnWidth(col.header, sheetData, col.header)
      }));
    }

    // Add to workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, sanitizeSheetName(sheetConfig.name));
  }

  if (onProgress) {
    onProgress(100);
  }

  // Generate and download file
  const finalFilename = generateFilename(filename, options.carrierName, 'xlsx');
  XLSX.writeFile(workbook, finalFilename);
};

/**
 * Export comprehensive carrier data to Excel with all data types
 */
export const exportCarrierDataToExcel = async (
  data: {
    profile?: Record<string, unknown>;
    violations?: Record<string, unknown>[];
    inspections?: Record<string, unknown>[];
    crashes?: Record<string, unknown>[];
    basics?: Record<string, unknown>[];
    vehicles?: Record<string, unknown>[];
  },
  carrierName?: string,
  dotNumber?: number,
  onProgress?: (progress: number) => void
): Promise<void> => {
  const sheets: ExcelSheetConfig[] = [];

  // Profile sheet
  if (data.profile) {
    sheets.push({
      name: 'Carrier Profile',
      data: [formatProfileForExcel(data.profile)],
    });
  }

  // BASIC Scores sheet
  if (data.basics && data.basics.length > 0) {
    sheets.push({
      name: 'BASIC Scores',
      data: data.basics,
      columns: basicScoresColumns,
    });
  }

  // Violations sheet
  if (data.violations && data.violations.length > 0) {
    sheets.push({
      name: 'Violations',
      data: data.violations,
      columns: violationsColumns,
    });
  }

  // Inspections sheet
  if (data.inspections && data.inspections.length > 0) {
    sheets.push({
      name: 'Inspections',
      data: data.inspections,
      columns: inspectionsColumns,
    });
  }

  // Crashes sheet
  if (data.crashes && data.crashes.length > 0) {
    sheets.push({
      name: 'Crashes',
      data: data.crashes,
      columns: crashesColumns,
    });
  }

  // Fleet/Vehicles sheet
  if (data.vehicles && data.vehicles.length > 0) {
    sheets.push({
      name: 'Fleet Vehicles',
      data: data.vehicles,
      columns: vehiclesColumns,
    });
  }

  // Summary statistics sheet
  sheets.push({
    name: 'Summary Statistics',
    data: [generateSummaryStats(data)],
  });

  await exportToExcel(sheets, 'carrier_data', {
    carrierName,
    dotNumber,
    includeMetadata: true,
  }, onProgress);
};

/**
 * Export analytics data to Excel
 */
export const exportAnalyticsToExcel = async (
  analyticsData: {
    violationTrends?: Record<string, unknown>[];
    crashTrends?: Record<string, unknown>[];
    inspectionTrends?: Record<string, unknown>[];
    basicTrends?: Record<string, unknown>[];
    stateDistribution?: Record<string, unknown>[];
    monthlyStats?: Record<string, unknown>[];
  },
  carrierName?: string,
  onProgress?: (progress: number) => void
): Promise<void> => {
  const sheets: ExcelSheetConfig[] = [];

  if (analyticsData.violationTrends && analyticsData.violationTrends.length > 0) {
    sheets.push({
      name: 'Violation Trends',
      data: analyticsData.violationTrends,
    });
  }

  if (analyticsData.crashTrends && analyticsData.crashTrends.length > 0) {
    sheets.push({
      name: 'Crash Trends',
      data: analyticsData.crashTrends,
    });
  }

  if (analyticsData.inspectionTrends && analyticsData.inspectionTrends.length > 0) {
    sheets.push({
      name: 'Inspection Trends',
      data: analyticsData.inspectionTrends,
    });
  }

  if (analyticsData.basicTrends && analyticsData.basicTrends.length > 0) {
    sheets.push({
      name: 'BASIC Score Trends',
      data: analyticsData.basicTrends,
    });
  }

  if (analyticsData.stateDistribution && analyticsData.stateDistribution.length > 0) {
    sheets.push({
      name: 'State Distribution',
      data: analyticsData.stateDistribution,
    });
  }

  if (analyticsData.monthlyStats && analyticsData.monthlyStats.length > 0) {
    sheets.push({
      name: 'Monthly Statistics',
      data: analyticsData.monthlyStats,
    });
  }

  await exportToExcel(sheets, 'analytics_data', { carrierName }, onProgress);
};

// ==========================================
// HELPER FUNCTIONS (must be defined before column definitions)
// ==========================================

/**
 * Format date for Excel
 */
const formatDate = (value: unknown): string => {
  if (!value) return '';
  try {
    const date = new Date(value as string | number | Date);
    if (isNaN(date.getTime())) return String(value);
    return date.toLocaleDateString('en-US');
  } catch {
    return String(value);
  }
};

/**
 * Format unit type
 */
const formatUnitType = (value: unknown): string => {
  const unitTypeMap: Record<number, string> = {
    9: 'Trailer',
    11: 'Truck/Tractor',
  };
  return unitTypeMap[value as number] || String(value || '');
};

/**
 * Get nested object value using dot notation
 */
const getNestedValue = (obj: Record<string, unknown>, path: string): unknown => {
  return path.split('.').reduce((current: unknown, key: string) => {
    return (current as Record<string, unknown>)?.[key];
  }, obj);
};

/**
 * Format value for Excel
 */
const formatValue = (value: unknown): string | number => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') return value;
  if (value instanceof Date) return value.toISOString();
  return String(value);
};

/**
 * Sanitize sheet name (Excel has restrictions)
 */
const sanitizeSheetName = (name: string): string => {
  // Excel sheet names: max 31 chars, no special chars
  return name
    .replace(/[\\/*?[\]]/g, '')
    .substring(0, 31);
};

/**
 * Calculate optimal column width based on content
 */
const calculateColumnWidth = (
  header: string,
  data: Record<string, unknown>[],
  key: string
): number => {
  const headerWidth = header.length;
  const maxDataWidth = data.reduce((max, row) => {
    const value = row[key];
    const length = String(value || '').length;
    return Math.max(max, length);
  }, 0);

  // Add some padding and cap at reasonable max
  return Math.min(Math.max(headerWidth, maxDataWidth) + 2, 50);
};

/**
 * Create metadata sheet with report information
 */
const createMetadataSheet = (options: ExcelExportOptions): XLSX.WorkSheet => {
  const metadata = [
    ['Report Information'],
    [''],
    ['Generated', new Date().toLocaleString()],
    ['Carrier Name', options.carrierName || 'N/A'],
    ['DOT Number', options.dotNumber?.toString() || 'N/A'],
    [''],
    ['Platform', 'Transportation Safety Platform'],
    ['Report Type', 'Carrier Data Export'],
    [''],
    ['Notes'],
    ['This report contains confidential carrier safety information.'],
    ['Data should be verified against official FMCSA sources.'],
  ];

  return XLSX.utils.aoa_to_sheet(metadata);
};

/**
 * Format carrier profile for Excel
 */
const formatProfileForExcel = (profile: Record<string, unknown>): Record<string, unknown> => {
  return {
    'DOT Number': profile.dotNumber,
    'Legal Name': profile.legalName,
    'DBA Name': profile.dbaName || '',
    'Status': profile.statusCode,
    'Allowed to Operate': profile.allowedToOperate === 'Y' ? 'Yes' : 'No',
    'Total Drivers': profile.totalDrivers,
    'Total Power Units': profile.totalPowerUnits,
    'Safety Rating': profile.safetyRating || 'Unrated',
    'Safety Rating Date': formatDate(profile.safetyRatingDate),
    'Total Crashes': profile.crashTotal,
    'Fatal Crashes': profile.fatalCrash,
    'Injury Crashes': profile.injCrash,
    'Tow-Away Crashes': profile.towawayCrash,
    'Driver Inspections': profile.driverInsp,
    'Driver OOS Inspections': profile.driverOosInsp,
    'Driver OOS Rate (%)': profile.driverOosRate,
    'Vehicle Inspections': profile.vehicleInsp,
    'Vehicle OOS Inspections': profile.vehicleOosInsp,
    'Vehicle OOS Rate (%)': profile.vehicleOosRate,
    'Street Address': profile.phyStreet,
    'City': profile.phyCity,
    'State': profile.phyState,
    'Zip Code': profile.phyZipcode,
    'Phone': profile.phone || '',
    'Email': profile.email || '',
    'EIN': profile.ein || '',
    'Common Authority': profile.commonAuthorityStatus || '',
    'Contract Authority': profile.contractAuthorityStatus || '',
    'Broker Authority': profile.brokerAuthorityStatus || '',
    'BIPD Insurance': profile.bipdInsuranceOnFile || '',
    'Cargo Insurance': profile.cargoInsuranceOnFile || '',
  };
};

/**
 * Generate summary statistics
 */
const generateSummaryStats = (data: {
  violations?: Record<string, unknown>[];
  inspections?: Record<string, unknown>[];
  crashes?: Record<string, unknown>[];
}): Record<string, unknown> => {
  const violations = data.violations || [];
  const inspections = data.inspections || [];
  const crashes = data.crashes || [];

  return {
    'Total Violations': violations.length,
    'Critical Violations': violations.filter((v) => v.severity === 'CRITICAL').length,
    'High Severity Violations': violations.filter((v) => v.severity === 'HIGH').length,
    'OOS Violations': violations.filter((v) => v.outOfServiceIndicator === 'Y').length,
    'Total Inspections': inspections.length,
    'Clean Inspections': inspections.filter((i) => (i.violTotal as number || 0) === 0).length,
    'OOS Inspections': inspections.filter((i) => (i.oosTotal as number || 0) > 0).length,
    'Total Crashes': crashes.length,
    'Fatal Crashes': crashes.filter((c) => Number(c.fatalities || 0) > 0).length,
    'Injury Crashes': crashes.filter((c) => Number(c.injuries || 0) > 0).length,
    'Hazmat Releases': crashes.filter((c) => c.hazmatReleased === 'Y').length,
    'Report Generated': new Date().toISOString(),
  };
};

// ==========================================
// COLUMN DEFINITIONS
// ==========================================

const violationsColumns: ExcelColumn[] = [
  { key: 'inspectionDate', header: 'Inspection Date', width: 15, transform: formatDate },
  { key: 'violationCode', header: 'Violation Code', width: 15 },
  { key: 'description', header: 'Description', width: 50 },
  { key: 'basicCategory', header: 'BASIC Category', width: 20 },
  { key: 'severity', header: 'Severity', width: 12 },
  { key: 'outOfServiceIndicator', header: 'Out of Service', width: 12, transform: (v) => v === 'Y' ? 'Yes' : 'No' },
  { key: 'inspectionState', header: 'State', width: 8 },
  { key: 'vin', header: 'VIN', width: 20 },
  { key: 'unitMake', header: 'Unit Make', width: 15 },
  { key: 'unitType', header: 'Unit Type', width: 12, transform: formatUnitType },
  { key: 'inspViolUnit', header: 'Violation Unit', width: 12 },
  { key: 'inspectionId', header: 'Inspection ID', width: 18 },
];

const inspectionsColumns: ExcelColumn[] = [
  { key: 'insp_date', header: 'Date', width: 15, transform: formatDate },
  { key: 'inspection_id', header: 'Inspection ID', width: 18 },
  { key: 'reportState', header: 'State', width: 8 },
  { key: 'inspLevelId', header: 'Level', width: 8 },
  { key: 'locationDesc', header: 'Location', width: 30 },
  { key: 'violTotal', header: 'Violations', width: 12 },
  { key: 'oosTotal', header: 'OOS Count', width: 12 },
  { key: 'shipperName', header: 'Shipper Name', width: 35 },
  { key: 'primaryVin', header: 'Primary VIN', width: 20 },
  { key: 'hazmatInsp', header: 'Hazmat', width: 10, transform: (v) => v === 'Y' ? 'Yes' : 'No' },
];

const crashesColumns: ExcelColumn[] = [
  { key: 'reportDate', header: 'Date', width: 15, transform: formatDate },
  { key: 'crashId', header: 'Crash ID', width: 15 },
  { key: 'reportState', header: 'State', width: 8 },
  { key: 'city', header: 'City', width: 20 },
  { key: 'location', header: 'Location', width: 30 },
  { key: 'vehicleIdentificationNumber', header: 'VIN', width: 20 },
  { key: 'fatalities', header: 'Fatalities', width: 12 },
  { key: 'injuries', header: 'Injuries', width: 12 },
  { key: 'towAway', header: 'Tow Away', width: 10, transform: (v) => v === 'Y' ? 'Yes' : 'No' },
  { key: 'hazmatReleased', header: 'Hazmat Released', width: 15, transform: (v) => v === 'Y' ? 'Yes' : 'No' },
  {
    key: 'severity',
    header: 'Severity',
    width: 15,
    transform: (_, row) => {
      if (Number(row?.fatalities || 0) > 0) return 'Fatal';
      if (Number(row?.injuries || 0) > 0) return 'Injury';
      if (row?.towAway === 'Y') return 'Tow-Away';
      return 'Property Damage';
    }
  },
];

const basicScoresColumns: ExcelColumn[] = [
  { key: 'basic.basicsType.basicsShortDesc', header: 'BASIC Category', width: 25 },
  { key: 'basic.basicsPercentile', header: 'Percentile', width: 12 },
  { key: 'basic.basicsViolationThreshold', header: 'Threshold', width: 12 },
  { key: 'basic.exceededFMCSAInterventionThreshold', header: 'Exceeded', width: 12, transform: (v) => v === 'Y' ? 'Yes' : 'No' },
  { key: 'basic.totalInspectionWithViolation', header: 'Insp w/ Violations', width: 18 },
  { key: 'basic.totalViolation', header: 'Total Violations', width: 15 },
  { key: 'basic.basicsRunDate', header: 'Last Updated', width: 15, transform: formatDate },
  { key: 'basic.estimated', header: 'Estimated', width: 12, transform: (v) => v ? 'Yes' : 'No' },
  { key: 'basic.riskLevel', header: 'Risk Level', width: 12 },
];

const vehiclesColumns: ExcelColumn[] = [
  { key: 'vin', header: 'VIN', width: 20 },
  { key: 'vinStatus', header: 'Status', width: 12 },
  { key: 'make', header: 'Make', width: 15 },
  { key: 'model', header: 'Model', width: 15 },
  { key: 'year', header: 'Year', width: 8 },
  { key: 'unitType', header: 'Type', width: 15, transform: formatUnitType },
  { key: 'inspectionCount', header: 'Inspections', width: 12 },
  { key: 'violationCount', header: 'Violations', width: 12 },
  { key: 'crashCount', header: 'Crashes', width: 10 },
  { key: 'firstSeen', header: 'First Seen', width: 15, transform: formatDate },
  { key: 'lastSeen', header: 'Last Seen', width: 15, transform: formatDate },
  { key: 'multiCarrier', header: 'Multi-Carrier', width: 12, transform: (v) => v ? 'Yes' : 'No' },
  { key: 'carrierCount', header: 'Carrier Count', width: 12 },
];
